import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  LockClosedIcon,
  SparklesIcon,
  ChartBarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../../context/WalletContext';

const tiers = [
  {
    name: 'Bronze',
    requirement: 1000,
    discount: 10,
    color: 'from-amber-700 to-amber-600',
    benefits: ['10% descuento en fees', 'Badge de perfil', 'Acceso básico'],
  },
  {
    name: 'Silver',
    requirement: 10000,
    discount: 20,
    color: 'from-slate-400 to-slate-300',
    benefits: ['20% descuento en fees', 'Soporte prioritario', 'Subastas exclusivas mensuales'],
  },
  {
    name: 'Gold',
    requirement: 50000,
    discount: 30,
    color: 'from-yellow-400 to-amber-500',
    benefits: ['30% descuento en fees', 'Subastas VIP', 'Curación prioritaria', 'Voto DAO'],
  },
  {
    name: 'Platinum',
    requirement: 100000,
    discount: 35,
    color: 'from-slate-300 to-indigo-400',
    benefits: ['35% descuento en fees', 'Todas las funciones Gold', 'Soporte dedicado', 'Eventos exclusivos'],
  },
];

const StakingPage: React.FC = () => {
  const { ndhBalance, tier } = useWallet();
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [activeTab, setActiveTab] = useState<'stake' | 'unstake'>('stake');

  // Mock staking data
  const stakedAmount = 15000;
  const pendingRewards = 125.5;
  const apr = 12.5;

  const currentTierIndex = tiers.findIndex(t => t.name.toUpperCase() === tier);
  const nextTier = currentTierIndex < tiers.length - 1 ? tiers[currentTierIndex + 1] : null;
  const progressToNextTier = nextTier
    ? Math.min(100, (stakedAmount / nextTier.requirement) * 100)
    : 100;

  const handleStake = () => {
    console.log('Staking:', stakeAmount);
    setStakeAmount('');
  };

  const handleUnstake = () => {
    console.log('Unstaking:', unstakeAmount);
    setUnstakeAmount('');
  };

  const handleClaimRewards = () => {
    console.log('Claiming rewards');
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <CurrencyDollarIcon className="w-8 h-8 text-primary-400" />
          Mi Staking
        </h1>
        <p className="text-gray-400">Stakea NDH para obtener beneficios exclusivos</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
        >
          <LockClosedIcon className="w-8 h-8 text-primary-400 mb-3" />
          <p className="text-2xl font-bold text-white">{stakedAmount.toLocaleString()}</p>
          <p className="text-gray-400 text-sm">NDH Stakeado</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
        >
          <SparklesIcon className="w-8 h-8 text-green-400 mb-3" />
          <p className="text-2xl font-bold text-green-400">{pendingRewards.toFixed(2)}</p>
          <p className="text-gray-400 text-sm">Rewards Pendientes</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
        >
          <ArrowTrendingUpIcon className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-2xl font-bold text-white">{apr}%</p>
          <p className="text-gray-400 text-sm">APR Actual</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
        >
          <ChartBarIcon className="w-8 h-8 text-purple-400 mb-3" />
          <p className="text-2xl font-bold text-white">{parseFloat(ndhBalance).toLocaleString()}</p>
          <p className="text-gray-400 text-sm">NDH Disponible</p>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Staking Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stake/Unstake Form */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700">
            <div className="flex border-b border-dark-700">
              <button
                onClick={() => setActiveTab('stake')}
                className={`flex-1 py-4 text-center font-medium transition-colors ${
                  activeTab === 'stake'
                    ? 'text-primary-400 border-b-2 border-primary-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Stakear
              </button>
              <button
                onClick={() => setActiveTab('unstake')}
                className={`flex-1 py-4 text-center font-medium transition-colors ${
                  activeTab === 'unstake'
                    ? 'text-primary-400 border-b-2 border-primary-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Retirar
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'stake' ? (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Cantidad a Stakear</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-xl focus:outline-none focus:border-primary-500"
                    />
                    <button
                      onClick={() => setStakeAmount(ndhBalance)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-primary-500/20 text-primary-400 rounded text-sm hover:bg-primary-500/30"
                    >
                      MAX
                    </button>
                  </div>
                  <p className="text-gray-500 text-sm mt-2">
                    Disponible: {parseFloat(ndhBalance).toLocaleString()} NDH
                  </p>
                  <button
                    onClick={handleStake}
                    disabled={!stakeAmount || parseFloat(stakeAmount) <= 0}
                    className="w-full mt-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                  >
                    Stakear NDH
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Cantidad a Retirar</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={unstakeAmount}
                      onChange={(e) => setUnstakeAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-xl focus:outline-none focus:border-primary-500"
                    />
                    <button
                      onClick={() => setUnstakeAmount(stakedAmount.toString())}
                      className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-primary-500/20 text-primary-400 rounded text-sm hover:bg-primary-500/30"
                    >
                      MAX
                    </button>
                  </div>
                  <p className="text-gray-500 text-sm mt-2">
                    Stakeado: {stakedAmount.toLocaleString()} NDH
                  </p>
                  <button
                    onClick={handleUnstake}
                    disabled={!unstakeAmount || parseFloat(unstakeAmount) <= 0}
                    className="w-full mt-4 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:bg-dark-600 disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg transition-colors font-medium"
                  >
                    Retirar NDH
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Rewards Panel */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Rewards Pendientes</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-3xl font-bold text-green-400">{pendingRewards.toFixed(2)} NDH</p>
                <p className="text-gray-500 text-sm">~$12.55 USD</p>
              </div>
              <button
                onClick={handleClaimRewards}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-medium"
              >
                Reclamar Rewards
              </button>
            </div>
            <p className="text-gray-400 text-sm">
              Los rewards se acumulan cada bloque y pueden ser reclamados en cualquier momento.
            </p>
          </div>
        </div>

        {/* Sidebar - Tier Info */}
        <div className="space-y-6">
          {/* Current Tier */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Tu Tier Actual</h3>
            <div className={`inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r ${
              tiers[currentTierIndex]?.color || 'from-gray-500 to-gray-600'
            } text-white font-bold text-lg mb-4`}>
              {tier || 'Básico'}
            </div>

            {nextTier && (
              <>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Progreso a {nextTier.name}</span>
                    <span className="text-white">{progressToNextTier.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${nextTier.color}`}
                      style={{ width: `${progressToNextTier}%` }}
                    />
                  </div>
                </div>
                <p className="text-gray-500 text-sm">
                  Necesitas {(nextTier.requirement - stakedAmount).toLocaleString()} NDH más
                </p>
              </>
            )}
          </div>

          {/* All Tiers */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Tiers Disponibles</h3>
            <div className="space-y-4">
              {tiers.map((t, idx) => {
                const isCurrentTier = t.name.toUpperCase() === tier;
                const isAchieved = stakedAmount >= t.requirement;

                return (
                  <div
                    key={t.name}
                    className={`p-4 rounded-lg border ${
                      isCurrentTier
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-dark-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r ${t.color} text-white`}>
                          {t.name}
                        </span>
                        {isCurrentTier && (
                          <CheckCircleIcon className="w-5 h-5 text-primary-400" />
                        )}
                      </div>
                      <span className="text-sm text-gray-400">{t.discount}% off</span>
                    </div>
                    <p className="text-white text-sm mb-2">
                      {t.requirement.toLocaleString()} NDH
                    </p>
                    <ul className="space-y-1">
                      {t.benefits.slice(0, 2).map((benefit, bidx) => (
                        <li key={bidx} className="text-xs text-gray-500 flex items-center gap-1">
                          <span className="w-1 h-1 bg-gray-500 rounded-full" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakingPage;
